import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, CalendarClock, CircleAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { statoAccount, type StatoAccount } from "@/lib/accesso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { invitiAllAcquistoNellApp } from "@/lib/iscrizione";
import { cn } from "@/lib/utils";

/**
 * A che punto sei, e come si passa alla versione completa.
 *
 * PERCHE' ESISTE
 * Davide, il 26/08/2026, dal suo account in prova: "non c'e' la possibilita' di
 * contattare Datacom per avere la versione perpetua dell'app". Aveva ragione, e
 * il buco era piu' grave di un pulsante mancante: l'UNICO collegamento a
 * /richiedi-accesso stava dentro il blocco che compare a prova FINITA. Cioe' la
 * porta per comprare si apriva solo dopo aver sbattuto contro il muro.
 *
 * Un installatore che nei sette giorni si trova bene e vuole comprare SUBITO
 * e' il cliente migliore che Datacom possa avere: non deve cercare un indirizzo
 * email, deve trovare un pulsante. Ora sta in Impostazioni e nel menu, sempre
 * visibile, e dice anche quanti giorni mancano - che e' l'altra domanda che uno
 * in prova si fa ogni volta che apre l'app.
 */

function giorni(n: number | undefined): string | null {
  if (n === undefined) return null;
  if (n <= 0) return "Ultimo giorno";
  if (n === 1) return "Resta 1 giorno";
  return `Restano ${n} giorni`;
}

/** Le parole cambiano con lo stato: la richiesta non e' la stessa cosa. */
function testi(stato: StatoAccount) {
  if (stato.tipo === "attivo") {
    return {
      titolo: "Account attivo",
      dettaglio: giorni(stato.giorniRimanenti)
        ? `Scade fra ${stato.giorniRimanenti} giorni`
        : "Senza scadenza",
      azione: "Aggiungi marchi",
      Icona: BadgeCheck,
      tono: "text-primary",
      forte: false,
    };
  }
  if (stato.tipo === "scaduto") {
    // Un cliente scaduto non e' una prova finita (07/09/2026): «rinnova»,
    // con la data, e i marchi che aveva restano in vista qui sotto.
    const r = stato.abbonamentoScaduto;
    if (r) {
      const il = r.il && !Number.isNaN(new Date(r.il).getTime())
        ? new Date(r.il).toLocaleDateString("it-IT")
        : null;
      return {
        // NIENTE «ABBONAMENTO» NE' «RINNOVO» IN VISTA (Apple, 22/09/2026,
        // Guideline 3.1.1): l'accesso lo attiva Datacom alle imprese clienti,
        // e le parole devono dire quello, non «abbonamento comprato fuori».
        titolo: "Accesso scaduto",
        dettaglio: il ? `Finito il ${il}: per continuare serve la riattivazione da Datacom` : "Per continuare serve la riattivazione da Datacom",
        azione: "Contatta Datacom",
        Icona: CircleAlert,
        tono: "text-destructive",
        forte: true,
      };
    }
    return {
      titolo: "Valutazione terminata",
      dettaglio: "Per continuare serve l'account installatore",
      azione: "Richiedi l'attivazione",
      Icona: CircleAlert,
      tono: "text-destructive",
      forte: true,
    };
  }
  return {
    titolo: "Periodo di valutazione",
    dettaglio: giorni(stato.giorniRimanenti) ?? "In corso",
    azione: "Richiedi l'attivazione",
    Icona: CalendarClock,
    tono: "text-primary",
    forte: true,
  };
}

/**
 * compatta = la riga del menu laterale; altrimenti la scheda delle impostazioni.
 */
export function TesseraAccesso({ compatta = false, onNaviga }: {
  compatta?: boolean;
  onNaviga?: () => void;
}) {
  const { profile } = useAuth();
  const stato = statoAccount(profile);
  // Senza profilo non si inventa uno stato: meglio niente che un'informazione
  // sbagliata sul proprio abbonamento.
  if (!stato) return null;

  const t = testi(stato);

  // Sull'iPhone lo stato si mostra, ma senza il passo verso la richiesta di
  // attivazione (Apple 3.1.1, 29/09): vedi src/lib/iscrizione.ts.
  const conRichiesta = invitiAllAcquistoNellApp();

  if (compatta && !conRichiesta) {
    return (
      <div className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm">
        <t.Icona className={cn("h-4 w-4 shrink-0", t.tono)} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-foreground">{t.titolo}</span>
          <span className="block truncate text-xs text-muted-foreground">{t.dettaglio}</span>
        </span>
      </div>
    );
  }

  if (compatta) {
    return (
      <Link
        to="/richiedi-accesso"
        onClick={onNaviga}
        className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors hover:bg-surface"
      >
        <t.Icona className={cn("h-4 w-4 shrink-0", t.tono)} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-foreground">{t.titolo}</span>
          <span className="block truncate text-xs text-muted-foreground">{t.dettaglio}</span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </Link>
    );
  }

  return (
    <section className="rounded-xl border border-border/60 bg-card p-5">
      <h2 className="mb-4 text-sm font-semibold">Il tuo accesso</h2>
      <div className="flex items-start gap-3">
        <t.Icona className={cn("mt-0.5 h-5 w-5 shrink-0", t.tono)} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">{t.titolo}</div>
          <div className="text-sm text-muted-foreground">{t.dettaglio}</div>
          {profile?.brands && profile.brands.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {profile.brands.map((b) => (
                <Badge key={b} variant="secondary" className="font-mono text-xs">
                  {b}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      {conRichiesta && (
        <>
          <Button
            asChild
            size="sm"
            variant={t.forte ? "default" : "outline"}
            className="mt-4 w-full gap-2 sm:w-auto"
          >
            <Link to="/richiedi-accesso" onClick={onNaviga}>
              {t.azione}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            La richiesta arriva a Datacom, che ti risponde per email.
          </p>
        </>
      )}
    </section>
  );
}
